/**
 * Where the website keeps your sign-in in this browser: your Torn key (if you typed one here)
 * and the Omni-Torn sign-in token, encrypted with AES-GCM. The encryption key is made by the
 * browser as "non-extractable" and kept in this site's IndexedDB: page code can use it but never
 * read it out, and the stored text is useless anywhere else.
 *
 * "Remember me on this device" keeps it in localStorage (stays after closing the browser);
 * otherwise sessionStorage (gone when the tab closes). If the browser has no IndexedDB (some
 * private windows), nothing is written at all: you stay signed in until you close the tab.
 */

const DB = 'omni-torn-site';
const STORE = 'keys';
const SLOT = 'omni-torn.signin';

let cryptoKey: Promise<CryptoKey | null> | null = null;

function openKey(): Promise<CryptoKey | null> {
  return (cryptoKey ??= new Promise<CryptoKey | null>((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB, 1);
    } catch {
      return resolve(null);
    }
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onerror = () => resolve(null);
    req.onsuccess = () => {
      const db = req.result;
      const get = db.transaction(STORE).objectStore(STORE).get('aes');
      get.onerror = () => resolve(null);
      get.onsuccess = async () => {
        if (get.result instanceof CryptoKey) return resolve(get.result);
        try {
          const k = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
          const put = db.transaction(STORE, 'readwrite').objectStore(STORE).put(k, 'aes');
          put.onsuccess = () => resolve(k);
          put.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      };
    };
  }));
}

const b64 = (b: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(b)));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** Kept only in memory when the browser can't store it safely. */
let memory: unknown = null;

export async function saveSecret(value: unknown, remember: boolean): Promise<'stored' | 'memory'> {
  memory = value;
  forgetStored();
  const k = await openKey();
  if (!k) return 'memory';
  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, k, new TextEncoder().encode(JSON.stringify(value)));
    (remember ? localStorage : sessionStorage).setItem(SLOT, `${b64(iv)}.${b64(data)}`);
    return 'stored';
  } catch {
    return 'memory';
  }
}

export async function loadSecret<T>(): Promise<{ value: T; remembered: boolean } | null> {
  if (memory) return { value: memory as T, remembered: false };
  let raw: string | null = null;
  let remembered = false;
  try {
    raw = sessionStorage.getItem(SLOT);
    if (!raw) {
      raw = localStorage.getItem(SLOT);
      remembered = !!raw;
    }
  } catch {
    return null;
  }
  if (!raw) return null;
  const k = await openKey();
  const [iv, data] = raw.split('.');
  if (!k || !iv || !data) return null;
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(iv) }, k, unb64(data));
    memory = JSON.parse(new TextDecoder().decode(plain));
    return { value: memory as T, remembered };
  } catch {
    // Can't be opened any more (site data partly cleared): start again.
    forgetStored();
    return null;
  }
}

function forgetStored(): void {
  try {
    sessionStorage.removeItem(SLOT);
    localStorage.removeItem(SLOT);
  } catch {
    /* no storage */
  }
}

export function forgetSecret(): void {
  memory = null;
  forgetStored();
}
